import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  MenuItem,
  InputAdornment,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Box,
  Paper,
  Divider,
  IconButton,
  Stack,
  GlobalStyles,
} from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import axiosInstance from "../axiosInstance";
import { API_ENDPOINTS } from "../config";
import BusinessIcon from "@mui/icons-material/Business";
import PublicIcon from "@mui/icons-material/Public";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CategoryIcon from "@mui/icons-material/Category";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import { motion } from "framer-motion";

const COLORS = {
  orange: "#d37d36",
  orangeDark: "#b8662b",
  blue: "#1e4b81",
  blueDark: "#102840",
  white: "#ffffff",
  bg: "#f7f9fc",
  text: "#1d2f43",
  secondary: "#64748b",
  muted: "#4a5568",
  border: "rgba(16, 40, 64, 0.10)",
  borderSoft: "rgba(16, 40, 64, 0.07)",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: { xs: "12px", sm: "13px" },
    bgcolor: COLORS.white,
    minHeight: { xs: 42, sm: 44 },
    boxShadow: "none",
    "& fieldset": {
      borderColor: "rgba(16,40,64,0.14)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(211,125,54,0.46)",
    },
    "&.Mui-focused fieldset": {
      borderColor: COLORS.orange,
      borderWidth: 2,
    },
  },
  "& .MuiInputBase-input": {
    py: { xs: 1.02, sm: 1.05 },
    fontSize: { xs: "0.82rem", sm: "0.84rem" },
    fontWeight: 650,
    color: COLORS.blueDark,
  },
  "& .MuiSelect-select": {
    py: { xs: 1.02, sm: 1.05 },
    fontSize: { xs: "0.82rem", sm: "0.84rem" },
    fontWeight: 650,
    color: COLORS.blueDark,
  },
  "& .MuiFormHelperText-root": {
    mx: 0.2,
    fontSize: "0.68rem",
    lineHeight: 1.25,
    mt: 0.35,
  },
};

const InfoItem = ({ children }) => (
  <Stack direction="row" spacing={1} alignItems="flex-start">
    <CheckCircleOutlineIcon
      sx={{ color: COLORS.orange, fontSize: 18, mt: "2px", flexShrink: 0 }}
    />
    <Typography
      sx={{
        color: "rgba(255,255,255,0.78)",
        fontSize: "0.84rem",
        lineHeight: 1.45,
        fontWeight: 650,
      }}
    >
      {children}
    </Typography>
  </Stack>
);

const LogoBlock = ({ compact = false }) => (
  <Stack
    direction="row"
    spacing={compact ? 1 : 1.25}
    alignItems="center"
    justifyContent={compact ? "center" : "flex-start"}
    sx={{ minWidth: 0 }}
  >
    <Box
      sx={{
        width: compact ? 44 : 48,
        height: compact ? 44 : 48,
        borderRadius: compact ? "15px" : "16px",
        bgcolor: COLORS.white,
        border: compact ? `1px solid ${COLORS.borderSoft}` : "none",
        boxShadow: compact ? "0 10px 24px rgba(16,40,64,0.08)" : "none",
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <Box
        component="img"
        src="/images/logo_simbolo.png"
        alt="DQPa"
        sx={{
          width: compact ? 34 : 36,
          height: compact ? 34 : 36,
          objectFit: "contain",
        }}
      />
    </Box>

    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          color: compact ? COLORS.blueDark : COLORS.white,
          fontSize: compact ? "1.32rem" : "1.35rem",
          fontWeight: 950,
          letterSpacing: "-0.045em",
          lineHeight: 1,
        }}
      >
        DQPa
      </Typography>
      <Typography
        sx={{
          color: compact ? COLORS.secondary : "rgba(255,255,255,0.64)",
          fontSize: compact ? "0.62rem" : "0.68rem",
          fontWeight: 800,
          letterSpacing: "0.055em",
          textTransform: "uppercase",
          mt: 0.35,
          whiteSpace: { xs: "normal", sm: "nowrap" },
        }}
      >
        Data Quality Process Assessment
      </Typography>
    </Box>
  </Stack>
);

const RegistroPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const planType = queryParams.get("plan") || "freemium";
  const isPremium = planType === "premium";

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: planType === "freemium" ? "ROLE_FREEMIUM" : "ROLE_PREMIUM",
    empresa: "",
    pais: "",
    sector: "",
    acceptPolicy: false,
  });

  const [paises, setPaises] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const fetchPaises = async () => {
      try {
        const response = await axios.get("https://restcountries.com/v3.1/all");
        const listaPaises = response.data
          .map((pais) => pais.translations?.spa?.common || pais.name?.common)
          .filter(Boolean)
          .sort();

        setPaises(listaPaises);
      } catch (error) {
        console.error("Error al obtener la lista de países, usando fallback:", error);
        setPaises([
          "España",
          "México",
          "Argentina",
          "Colombia",
          "Chile",
          "Perú",
          "Estados Unidos",
          "Otro",
        ]);
      }
    };

    fetchPaises();

    setSectores([
      "Tecnología",
      "Finanzas",
      "Salud",
      "Educación",
      "Manufactura",
      "Consultoría",
      "Retail",
      "Administración pública",
      "Energía",
      "Otro",
    ]);
  }, []);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      role: planType === "freemium" ? "ROLE_FREEMIUM" : "ROLE_PREMIUM",
    }));
  }, [planType]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (error) setError("");
    if (success) setSuccess("");
  };

  const validatePassword = (password) => {
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&.])[A-Za-z\d@$!%*?&.]{8,}$/;
    return regex.test(password);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (isPremium && (!formData.empresa || !formData.pais || !formData.sector)) {
      setError("Para el plan Premium, todos los campos de organización son obligatorios.");
      return;
    }

    if (!formData.email || !formData.password) {
      setError("El correo y la contraseña son obligatorios.");
      return;
    }

    if (!validatePassword(formData.password)) {
      setError("La contraseña no cumple los requisitos de seguridad.");
      return;
    }

    if (!formData.acceptPolicy) {
      setError("Debes aceptar la política de privacidad para continuar.");
      return;
    }

    setLoading(true);

    try {
      await axiosInstance.post(API_ENDPOINTS.auth.register, formData);

      setSuccess(
        "Registro exitoso. Revisa tu correo, incluida la carpeta de spam, para confirmar tu cuenta."
      );

      setTimeout(() => navigate("/login"), 5000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Error al registrar usuario. Intenta nuevamente más tarde."
      );
    } finally {
      setLoading(false);
    }
  };

  const planLabel = isPremium ? "Plan Premium" : "Plan Freemium";

  return (
    <>
      <GlobalStyles
        styles={{
          html: {
            fontSize: "14.5px",
            overflowX: "hidden",
          },
          body: {
            margin: 0,
            overflowX: "hidden",
            backgroundColor: COLORS.bg,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
          "#root": {
            minHeight: "100vh",
            overflowX: "hidden",
          },
          "*": {
            boxSizing: "border-box",
          },
          "@media (max-width: 600px)": {
            html: { fontSize: "14px" },
          },
        }}
      />

      <Box
        component="main"
        sx={{
          minHeight: { xs: "100svh", lg: "100vh" },
          width: "100%",
          overflowX: "hidden",
          overflowY: { xs: "auto", lg: isPremium ? "auto" : "hidden" },
          bgcolor: COLORS.bg,
          display: "flex",
          alignItems: "stretch",
        }}
      >
        <Grid container sx={{ minHeight: { xs: "100svh", lg: "100vh" } }}>
          <Grid
            item
            xs={12}
            lg={5.4}
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              justifyContent: "center",
              px: { lg: 5.5, xl: 7 },
              py: 4,
              bgcolor: COLORS.blueDark,
              color: COLORS.white,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                opacity: 0.12,
                backgroundImage:
                  "linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)",
                backgroundSize: "48px 48px",
              }}
            />

            <Box
              sx={{
                position: "absolute",
                right: -120,
                bottom: -120,
                width: 300,
                height: 300,
                borderRadius: "50%",
                bgcolor: "rgba(211,125,54,0.20)",
              }}
            />

            <motion.div
              initial={{ opacity: 0, x: -18 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              style={{
                width: "100%",
                maxWidth: 460,
                position: "relative",
                zIndex: 1,
              }}
            >
              <Stack spacing={3}>
                <LogoBlock />

                <Box>
                  <Typography
                    component="h1"
                    sx={{
                      fontSize: { lg: "2.25rem", xl: "2.55rem" },
                      lineHeight: 1.06,
                      letterSpacing: "-0.05em",
                      fontWeight: 950,
                      maxWidth: 430,
                    }}
                  >
                    Cree su cuenta para evaluar gobierno y gestión de{" "}
                    <Box component="span" sx={{ color: COLORS.orange }}>
                      datos
                    </Box>
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1.55,
                      color: "rgba(255,255,255,0.72)",
                      fontSize: "0.92rem",
                      lineHeight: 1.58,
                      maxWidth: 420,
                    }}
                  >
                    Comience a medir sus procesos de datos y convierta los
                    resultados en acciones de mejora.
                  </Typography>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: "22px",
                    bgcolor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.12)",
                  }}
                >
                  <Stack spacing={1.15}>
                    <InfoItem>
                      Evaluaciones alineadas con UNE 0077, 0078, 0079 y 0080.
                    </InfoItem>
                    <InfoItem>
                      Resultados claros para perfiles técnicos y de dirección.
                    </InfoItem>
                    <InfoItem>Propuestas de mejora priorizadas.</InfoItem>
                  </Stack>
                </Paper>

                <Stack direction="row" spacing={0.9} flexWrap="wrap" useFlexGap>
                  {["Gobierno", "Gestión", "Calidad", "Madurez"].map((item) => (
                    <Box
                      key={item}
                      sx={{
                        px: 1.2,
                        py: 0.58,
                        borderRadius: "999px",
                        bgcolor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.12)",
                        color: "rgba(255,255,255,0.74)",
                        fontSize: "0.68rem",
                        fontWeight: 850,
                      }}
                    >
                      {item}
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </motion.div>
          </Grid>

          <Grid
            item
            xs={12}
            lg={6.6}
            sx={{
              minHeight: { xs: "100svh", lg: "100vh" },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: { xs: 1.6, sm: 2.5, md: 4 },
              py: { xs: 2.4, sm: 3, md: 4, lg: isPremium ? 2.2 : 3 },
              bgcolor: COLORS.bg,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: "easeOut", delay: 0.05 }}
              style={{ width: "100%", maxWidth: isPremium ? 500 : 390 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: {
                    xs: 1.9,
                    sm: isPremium ? 2.25 : 2.55,
                    md: isPremium ? 2.35 : 2.65,
                  },
                  borderRadius: { xs: "22px", sm: "24px" },
                  border: `1px solid ${COLORS.border}`,
                  bgcolor: COLORS.white,
                  boxShadow: "0 18px 50px rgba(16,40,64,0.08)",
                }}
              >
                <Box
                  sx={{
                    display: { xs: "flex", lg: "none" },
                    justifyContent: "center",
                    mb: { xs: 1.6, sm: 1.8 },
                  }}
                >
                  <LogoBlock compact />
                </Box>

                <Box sx={{ textAlign: "center", mb: isPremium ? 1.35 : 2 }}>
                  <Box
                    sx={{
                      width: { xs: 38, sm: 42 },
                      height: { xs: 38, sm: 42 },
                      mx: "auto",
                      mb: 1.05,
                      borderRadius: "15px",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "rgba(30,75,129,0.06)",
                      color: COLORS.blue,
                      border: "1px solid rgba(30,75,129,0.09)",
                    }}
                  >
                    <PersonAddAltOutlinedIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
                  </Box>

                  <Typography
                    component="h2"
                    sx={{
                      color: COLORS.blueDark,
                      fontWeight: 950,
                      fontSize: { xs: "1.26rem", sm: "1.5rem" },
                      letterSpacing: "-0.04em",
                      lineHeight: 1.1,
                      mb: 0.55,
                    }}
                  >
                    Crear cuenta
                  </Typography>

                  <Typography
                    sx={{
                      color: COLORS.secondary,
                      fontSize: { xs: "0.78rem", sm: "0.82rem" },
                      lineHeight: 1.4,
                    }}
                  >
                    {isPremium
                      ? "Complete los datos para activar el acceso Premium."
                      : "Empiece con una evaluación inicial gratuita."}
                  </Typography>

                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mt: 0.9,
                      px: 1.2,
                      py: 0.45,
                      borderRadius: "999px",
                      bgcolor: "rgba(211,125,54,0.08)",
                      border: "1px solid rgba(211,125,54,0.15)",
                      color: COLORS.blueDark,
                      fontSize: "0.68rem",
                      fontWeight: 900,
                    }}
                  >
                    {planLabel}
                  </Box>
                </Box>

                <Box component="form" noValidate onSubmit={handleRegister}>
                  <Stack spacing={isPremium ? { xs: 1, sm: 1.08 } : { xs: 1.25, sm: 1.45 }}>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 850,
                          mb: 0.48,
                          color: COLORS.blueDark,
                          fontSize: "0.76rem",
                        }}
                      >
                        Correo electrónico
                      </Typography>
                      <TextField
                        required
                        fullWidth
                        name="email"
                        autoComplete="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="tu@empresa.com"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailOutlinedIcon
                                sx={{ color: COLORS.secondary, fontSize: 18 }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputSx}
                      />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 850,
                          mb: 0.48,
                          color: COLORS.blueDark,
                          fontSize: "0.76rem",
                        }}
                      >
                        Contraseña
                      </Typography>
                      <TextField
                        required
                        fullWidth
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="••••••••"
                        helperText="Mín. 8 caracteres, 1 mayúscula, 1 número, 1 símbolo."
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlinedIcon
                                sx={{ color: COLORS.secondary, fontSize: 18 }}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                size="small"
                                sx={{
                                  color: COLORS.secondary,
                                  "&:hover": {
                                    color: COLORS.orange,
                                    bgcolor: "rgba(211,125,54,0.08)",
                                  },
                                }}
                              >
                                {showPassword ? (
                                  <VisibilityOff fontSize="small" />
                                ) : (
                                  <Visibility fontSize="small" />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={inputSx}
                      />
                    </Box>

                    {isPremium && (
                      <>
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 850,
                              mb: 0.48,
                              color: COLORS.blueDark,
                              fontSize: "0.76rem",
                            }}
                          >
                            Nombre de la empresa
                          </Typography>
                          <TextField
                            required
                            fullWidth
                            name="empresa"
                            value={formData.empresa}
                            onChange={handleChange}
                            placeholder="Nombre de la organización"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <BusinessIcon
                                    sx={{ color: COLORS.secondary, fontSize: 18 }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                            sx={inputSx}
                          />
                        </Box>

                        <Grid container spacing={1.05}>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              sx={{
                                fontWeight: 850,
                                mb: 0.48,
                                color: COLORS.blueDark,
                                fontSize: "0.76rem",
                              }}
                            >
                              País
                            </Typography>
                            <TextField
                              select
                              required
                              fullWidth
                              name="pais"
                              value={formData.pais}
                              onChange={handleChange}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PublicIcon
                                      sx={{ color: COLORS.secondary, fontSize: 18 }}
                                    />
                                  </InputAdornment>
                                ),
                              }}
                              sx={inputSx}
                            >
                              {paises.map((p) => (
                                <MenuItem key={p} value={p}>
                                  {p}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography
                              sx={{
                                fontWeight: 850,
                                mb: 0.48,
                                color: COLORS.blueDark,
                                fontSize: "0.76rem",
                              }}
                            >
                              Sector
                            </Typography>
                            <TextField
                              select
                              required
                              fullWidth
                              name="sector"
                              value={formData.sector}
                              onChange={handleChange}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <CategoryIcon
                                      sx={{ color: COLORS.secondary, fontSize: 18 }}
                                    />
                                  </InputAdornment>
                                ),
                              }}
                              sx={inputSx}
                            >
                              {sectores.map((s) => (
                                <MenuItem key={s} value={s}>
                                  {s}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </Grid>
                      </>
                    )}

                    <FormControlLabel
                      control={
                        <Checkbox
                          name="acceptPolicy"
                          checked={formData.acceptPolicy}
                          onChange={handleChange}
                          size="small"
                          sx={{
                            color: COLORS.blue,
                            pt: 0,
                            flexShrink: 0,
                            "&.Mui-checked": { color: COLORS.orange },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            color: COLORS.secondary,
                            fontSize: { xs: "0.72rem", sm: "0.74rem" },
                            lineHeight: 1.35,
                          }}
                        >
                          He leído y acepto la{" "}
                          <Link
                            href="/politica"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: COLORS.blue,
                              fontWeight: 850,
                              textDecoration: "none",
                              "&:hover": {
                                color: COLORS.orange,
                                textDecoration: "underline",
                              },
                            }}
                          >
                            Política de Privacidad
                          </Link>
                          .
                        </Typography>
                      }
                      sx={{ alignItems: "flex-start", m: 0 }}
                    />

                    {error && (
                      <Alert
                        severity="error"
                        sx={{
                          borderRadius: "13px",
                          fontWeight: 650,
                          fontSize: "0.78rem",
                          py: 0.55,
                        }}
                      >
                        {error}
                      </Alert>
                    )}

                    {success && (
                      <Alert
                        severity="success"
                        sx={{
                          borderRadius: "13px",
                          fontWeight: 650,
                          fontSize: "0.78rem",
                          py: 0.55,
                        }}
                      >
                        {success}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                      endIcon={!loading ? <ArrowForwardIcon /> : null}
                      sx={{
                        mt: 0.1,
                        py: 1.08,
                        minHeight: 42,
                        borderRadius: "13px",
                        textTransform: "none",
                        fontWeight: 900,
                        fontSize: "0.88rem",
                        bgcolor: COLORS.blue,
                        boxShadow: "0 12px 26px rgba(30,75,129,0.18)",
                        transition: "all .2s ease",
                        "&:hover": {
                          bgcolor: COLORS.blueDark,
                          boxShadow: "0 16px 34px rgba(16,40,64,0.24)",
                          transform: "translateY(-1px)",
                        },
                        "&.Mui-disabled": {
                          bgcolor: "rgba(30,75,129,0.45)",
                          color: COLORS.white,
                        },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "Crear cuenta"
                      )}
                    </Button>
                  </Stack>
                </Box>

                <Divider
                  sx={{
                    my: isPremium ? { xs: 1.45, sm: 1.65 } : { xs: 1.8, sm: 2.1 },
                    borderColor: COLORS.borderSoft,
                  }}
                />

                <Typography
                  align="center"
                  sx={{ color: COLORS.secondary, fontSize: "0.8rem", lineHeight: 1.4 }}
                >
                  ¿Ya tienes una cuenta?{" "}
                  <Link
                    component={RouterLink}
                    to="/login"
                    sx={{
                      textDecoration: "none",
                      color: COLORS.orange,
                      fontWeight: 900,
                      "&:hover": { color: COLORS.orangeDark },
                    }}
                  >
                    Iniciar sesión
                  </Link>
                </Typography>
              </Paper>

              <Typography
                align="center"
                sx={{
                  mt: 1.3,
                  px: { xs: 1, sm: 0 },
                  color: COLORS.secondary,
                  fontSize: "0.68rem",
                  lineHeight: 1.4,
                }}
              >
                Cuenta protegida y datos tratados conforme a la{" "}
                <Link
                  component={RouterLink}
                  to="/politica"
                  sx={{
                    color: COLORS.blue,
                    fontWeight: 800,
                    textDecoration: "none",
                    "&:hover": {
                      color: COLORS.orange,
                      textDecoration: "underline",
                    },
                  }}
                >
                  Política de Privacidad
                </Link>
                .
              </Typography>
            </motion.div>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default RegistroPage;
